import changesets from "diff-json";
import queryLogic from "./queryLogic";

const sanitizeQueryFilter = (items) => {
  items.forEach(x => {
    if (x.items && Array.isArray(x.items)) {
      sanitizeQueryFilter(x.items);
    }

    if (x.fields) {
      delete x.fields;
    }
  });
}

/**
 * 
 * @param {Array} defaultFilters 
 * @param {Array} originalDefaultFilters 
 * @param {Array} filters 
 * @param {Array} columns 
 * @returns 
 */
const setDefaultFilters = (defaultFilters, originalDefaultFilters, filters, columns) => {
  sanitizeQueryFilter(defaultFilters.items);

  let defaultFilterChanged = false;
  const defaultFiltersDiff = changesets.diff(defaultFilters, originalDefaultFilters);

  if (defaultFiltersDiff && defaultFiltersDiff.length) {
    filters = _.cloneDeep(defaultFilters);
    originalDefaultFilters = _.cloneDeep(defaultFilters);
    defaultFilterChanged = true;
  } else {
    let currentFilters = _.cloneDeep(filters);
    queryLogic.updateQueryFilters(columns, currentFilters);

    const filtersDiff = changesets.diff(currentFilters, filters);

    if (filtersDiff && filtersDiff.length) {
      filters = currentFilters;
    }
  }

  return {
    filters: filters,
    originalDefaultFilters: originalDefaultFilters,
    defaultFilterChanged: defaultFilterChanged
  }
}

/* SS05684. A listing's saved user filter (the localStorage preference) snapshots the WHOLE filter
   tree, including the units the DESIGN owns - a `custom` unit carries its script text, a
   `formula` unit its formula - and the server runs whatever the client posts (FilterProcessor
   "custom" executes queryUnit.script from the request). So after the listing design changed, a
   preference kept replaying the OLD script until the filter was removed and the page reloaded.

   Re-seats every design-owned unit of the preference from the current definition, matched by unit
   id: the definition's keys win (script, formula, displayName...), the user's runtime `payload` is
   kept; design-owned units the definition no longer has are dropped, and definition units the
   preference lacks are appended. The user's own field units and groups are left exactly as they
   are. Returns { filters, changed }. */
const isDesignOwnedUnit = (unit) =>
  Boolean(unit) && (unit.type === "custom" || unit.type === "formula");

const collectDesignOwnedUnits = (items, into = new Map()) => {
  (Array.isArray(items) ? items : []).forEach(unit => {
    if (!unit) return;
    if (isDesignOwnedUnit(unit) && unit.id !== null && unit.id !== undefined) {
      into.set(String(unit.id), unit);
    }
    if (Array.isArray(unit.items)) {
      collectDesignOwnedUnits(unit.items, into);
    }
  });
  return into;
};

const refreshDesignOwnedUnits = (preferenceFilters, definitionFilters) => {
  const preferenceItems = preferenceFilters && Array.isArray(preferenceFilters.items)
    ? preferenceFilters.items
    : [];
  const definition = collectDesignOwnedUnits(
    definitionFilters && Array.isArray(definitionFilters.items) ? definitionFilters.items : []
  );
  const seen = new Set();

  const reseat = (units) => units.reduce((out, unit) => {
    if (!unit) return out;

    if (isDesignOwnedUnit(unit)) {
      const key = unit.id !== null && unit.id !== undefined ? String(unit.id) : null;
      const current = key !== null ? definition.get(key) : null;
      if (!current) return out; // the designer removed it

      seen.add(key);
      const next = _.cloneDeep(current);
      if (unit.payload !== undefined) next.payload = unit.payload; // the user's runtime value
      out.push(next);
      return out;
    }

    out.push(
      Array.isArray(unit.items)
        ? { ..._.cloneDeep(unit), items: reseat(unit.items) }
        : _.cloneDeep(unit)
    );
    return out;
  }, []);

  const items = reseat(preferenceItems);
  definition.forEach((unit, key) => {
    if (!seen.has(key)) items.push(_.cloneDeep(unit)); // newly designed
  });

  const filters = { ...(preferenceFilters || {}), items };
  return { filters, changed: !_.isEqual(filters, preferenceFilters) };
};

export default {
  setDefaultFilters, setDefaultFilters,
  isDesignOwnedUnit,
  refreshDesignOwnedUnits
}